import { useCallback, useEffect, useState } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { isEqual } from 'lodash';

import { useCourses } from '../contexts/courses/context';
import CourseInterface from '../interfaces/courses';

const useSortableCourses = () => {
  const { fetchCourses, updateOrder } = useCourses();
  const [courses, setCourses] = useState<CourseInterface[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [originalOrders, setOriginalOrders] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const { courses: coursesFetched } = await fetchCourses({ limit: 0, courseSort: 'custom' });
      setCourses(coursesFetched);
      const ids = coursesFetched.map((course) => course.id);
      setOriginalOrders(ids);
      setHasChanges(false);
    } finally {
      setIsLoading(false);
    }
  };

  const onDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setCourses((prev) => {
        const oldIndex = prev.findIndex((c) => c.id === active.id);
        const newIndex = prev.findIndex((c) => c.id === over.id);
        return oldIndex !== newIndex ? arrayMove(prev, oldIndex, newIndex) : prev;
      });
    }
  }, [setCourses]);

  const saveOrder = async () => {
    setIsSaving(true);
    try {
      const orderPayload = courses
        .map((course, position) => ({ id: course.id, position: position + 1 }));

      await updateOrder(orderPayload);
    } finally {
      setIsSaving(false);
      load();
    }
  };

  useEffect(() => {
    const currentOrder = courses.map((course) => course.id);
    setHasChanges(!isEqual(currentOrder, originalOrders));
  }, [courses, originalOrders]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return {
    courses,
    hasChanges,
    isLoading,
    isSaving,
    onDragEnd,
    saveOrder,
  };
};

export default useSortableCourses;
